import React, { useEffect, useState } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import {
    Users,
    BookOpen,
    Calendar,
    BarChart,
    Settings,
    LogOut,
    UserCheck,
    Award,
    Clock,
    TrendingUp,
    Shield,
    Mail,
    Bell,
    Activity,
    Briefcase,
    Home,
    Image,
    Zap,
    Book,
    Download,
    RefreshCw
} from "react-feather";
import { Chart as ChartJS, registerables } from 'chart.js';
import { Line, Bar, Doughnut, Pie } from "react-chartjs-2";
import { auth, db, getAnalyticsData } from "shared/utils/firebase";
import {
    collection,
    getDocs,
    doc,
    getDoc,
    query,
    where,
    orderBy,
    limit
} from "firebase/firestore";
import styles from "./AdminDashboard.module.css";

ChartJS.register(...registerables);

function AdminDashboard({ userDetails, auth: isAuthenticated }) {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState("overview");
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalCourses: 0,
        totalEvents: 0,
        activeUsers: 0,
        newUsersToday: 0,
        totalEnrollments: 0,
        totalTests: 0,
        totalProjects: 0,
        totalModules: 0,
        totalAnnouncements: 0,
        totalJobs: 0,
        totalContacts: 0,
        totalCompanies: 0,
        totalGallery: 0,
        adminCount: 0,
        studentCount: 0,
        professionalCount: 0,
        deletedUsers: 0,
        deactivatedUsers: 0,
        publishedCourses: 0,
        draftCourses: 0,
        upcomingEvents: 0,
        pastEvents: 0,
        pendingContacts: 0,
        userProjects: 0,
        adminProjects: 0
    });
    const [recentUsers, setRecentUsers] = useState([]);
    const [recentActivities, setRecentActivities] = useState([]);

    // Analytics state
    const [analyticsData, setAnalyticsData] = useState(null);
    const [timeRange, setTimeRange] = useState("30d");
    const [loading, setLoading] = useState(true);

    // Check if user is admin
    const isAdmin = userDetails?.role === 'Admin' || userDetails?.role === 'admin' || userDetails?.isAdmin === true;

    useEffect(() => {
        if (isAuthenticated && isAdmin) {
            fetchDashboardData();
        }
    }, [isAuthenticated, isAdmin]);

    // Refetch analytics when time range changes
    useEffect(() => {
        if (analyticsData) {
            // Time range affects calculations, no need to refetch
        }
    }, [timeRange]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            // Fetch users
            const usersSnapshot = await getDocs(collection(db, "users"));
            const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Fetch courses
            const coursesSnapshot = await getDocs(collection(db, "courses"));
            const courses = coursesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Fetch events
            const eventsSnapshot = await getDocs(collection(db, "events"));
            const events = eventsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Fetch all collections
            const [testResultsSnapshot, projectsSnapshot, modulesSnapshot, announcementsSnapshot,
                jobsSnapshot, contactsSnapshot, companiesSnapshot, gallerySnapshot, testsSnapshot] = await Promise.all([
                    getDocs(collection(db, "testResults")),
                    getDocs(collection(db, "projects")),
                    getDocs(collection(db, "textModules")),
                    getDocs(collection(db, "announcements")),
                    getDocs(collection(db, "jobs")),
                    getDocs(collection(db, "contactRequests")),
                    getDocs(collection(db, "companies")),
                    getDocs(collection(db, "gallery")),
                    getDocs(collection(db, "tests"))
                ]);

            const testResults = testResultsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const projects = projectsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const modules = modulesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const announcements = announcementsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const jobs = jobsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const contacts = contactsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const companies = companiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const gallery = gallerySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const tests = testsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Calculate statistics
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const newUsersToday = users.filter(user => {
                if (user.createdAt) {
                    const userDate = user.createdAt.toDate();
                    userDate.setHours(0, 0, 0, 0);
                    return userDate.getTime() === today.getTime();
                }
                return false;
            }).length;

            const activeUsers = users.filter(user => {
                if (user.lastLogin) {
                    const lastLogin = user.lastLogin.toDate();
                    const daysSinceLogin = (today - lastLogin) / (1000 * 60 * 60 * 24);
                    return daysSinceLogin <= 7; // Active within last 7 days
                }
                return false;
            }).length;

            // User statistics
            const adminCount = users.filter(u => u.role === 'Admin').length;
            const studentCount = users.filter(u => u.role === 'Student').length;
            const professionalCount = users.filter(u => u.role === 'Professional').length;
            const deletedUsers = users.filter(u => u.deleted === true).length;
            const deactivatedUsers = users.filter(u => u.deactivated === true).length;

            // Course statistics
            const publishedCourses = courses.filter(c => c.isPublished === true).length;
            const draftCourses = courses.filter(c => !c.isPublished).length;

            // Event statistics
            const now = new Date();
            const upcomingEvents = events.filter(e => {
                const eventDate = e.date?.toDate ? e.date.toDate() : new Date(e.date);
                return eventDate >= now;
            }).length;
            const pastEvents = events.filter(e => {
                const eventDate = e.date?.toDate ? e.date.toDate() : new Date(e.date);
                return eventDate < now;
            }).length;

            // Contact statistics
            const pendingContacts = contacts.filter(c => c.status === 'pending' || !c.status).length;

            // Project statistics
            const userProjects = projects.filter(p => p.refUser).length;
            const adminProjects = projects.filter(p => !p.refUser).length;

            setStats({
                totalUsers: users.length,
                totalCourses: courses.length,
                totalEvents: events.length,
                activeUsers: activeUsers,
                newUsersToday: newUsersToday,
                totalEnrollments: testResults.length,
                totalTests: tests.length,
                totalProjects: projects.length,
                totalModules: modules.length,
                totalAnnouncements: announcements.length,
                totalJobs: jobs.length,
                totalContacts: contacts.length,
                totalCompanies: companies.length,
                totalGallery: gallery.length,
                adminCount,
                studentCount,
                professionalCount,
                deletedUsers,
                deactivatedUsers,
                publishedCourses,
                draftCourses,
                upcomingEvents,
                pastEvents,
                pendingContacts,
                userProjects,
                adminProjects
            });

            // Get recent users (last 5)
            const recentUsersList = users
                .sort((a, b) => {
                    const dateA = a.createdAt ? a.createdAt.toDate() : new Date(0);
                    const dateB = b.createdAt ? b.createdAt.toDate() : new Date(0);
                    return dateB - dateA;
                })
                .slice(0, 5);

            setRecentUsers(recentUsersList);

            // Mock recent activities for now
            setRecentActivities([
                { id: 1, action: "New user registered", user: "John Doe", time: "2 hours ago", type: "user" },
                { id: 2, action: "Course created", course: "Advanced React", time: "4 hours ago", type: "course" },
                { id: 3, action: "Event scheduled", event: "Web Development Workshop", time: "6 hours ago", type: "event" },
                { id: 4, action: "User completed test", user: "Jane Smith", test: "JavaScript Basics", time: "8 hours ago", type: "test" },
                { id: 5, action: "Course enrollment", user: "Mike Johnson", course: "Python Fundamentals", time: "1 day ago", type: "enrollment" }
            ]);

            // Set analytics data for charts
            setAnalyticsData({
                users: users,
                courses: courses,
                events: events,
                testResults: testResults,
                projects: projects
            });

        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    const getTimeRangeDays = () => {
        switch (timeRange) {
            case "7d": return 7;
            case "30d": return 30;
            case "90d": return 90;
            case "1y": return 365;
            default: return 30;
        }
    };

    const filterDataByTimeRange = (data, dateField = 'createdAt') => {
        const days = getTimeRangeDays();
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);

        return data.filter(item => {
            const itemDate = item[dateField];
            if (!itemDate) return false;

            const date = itemDate.toDate ? itemDate.toDate() : new Date(itemDate);
            return date >= cutoffDate;
        });
    };

    const calculateUserGrowth = () => {
        if (!analyticsData?.users) return [];

        const filteredUsers = filterDataByTimeRange(analyticsData.users);
        const days = getTimeRangeDays();
        const growthData = [];

        for (let i = days - 1; i >= 0; i--) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            date.setHours(0, 0, 0, 0);

            const usersOnDate = filteredUsers.filter(user => {
                const userDate = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
                userDate.setHours(0, 0, 0, 0);
                return userDate.getTime() === date.getTime();
            }).length;

            growthData.push({
                date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                count: usersOnDate
            });
        }

        return growthData;
    };

    const calculateCourseStats = () => {
        if (!analyticsData?.courses) return { total: 0, published: 0, drafts: 0, byCategory: {} };

        const courses = analyticsData.courses;
        const stats = {
            total: courses.length,
            published: courses.filter(c => c.isPublished).length,
            drafts: courses.filter(c => !c.isPublished).length,
            byCategory: {}
        };

        courses.forEach(course => {
            const category = course.category || 'Uncategorized';
            stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
        });

        return stats;
    };

    const calculateEventStats = () => {
        if (!analyticsData?.events) return { total: 0, upcoming: 0, past: 0, byType: {} };

        const events = analyticsData.events;
        const now = new Date();

        const stats = {
            total: events.length,
            upcoming: events.filter(e => new Date(e.date) >= now).length,
            past: events.filter(e => new Date(e.date) < now).length,
            byType: {}
        };

        events.forEach(event => {
            const type = event.type || 'Other';
            stats.byType[type] = (stats.byType[type] || 0) + 1;
        });

        return stats;
    };

    const calculateTestStats = () => {
        if (!analyticsData?.testResults) return { total: 0, averageScore: 0, byCategory: {} };

        const testResults = analyticsData.testResults;
        const stats = {
            total: testResults.length,
            averageScore: 0,
            byCategory: {}
        };

        if (testResults.length > 0) {
            const totalScore = testResults.reduce((sum, result) => sum + (result.score || 0), 0);
            stats.averageScore = Math.round(totalScore / testResults.length);
        }

        testResults.forEach(result => {
            const category = result.category || 'Other';
            stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
        });

        return stats;
    };

    const calculateProjectStats = () => {
        if (!analyticsData?.projects) return { total: 0, userProjects: 0, adminProjects: 0, byCategory: {} };

        const projects = analyticsData.projects;
        const stats = {
            total: projects.length,
            userProjects: projects.filter(p => p.refUser).length,
            adminProjects: projects.filter(p => !p.refUser).length,
            byCategory: {}
        };

        projects.forEach(project => {
            const category = project.category || 'Uncategorized';
            stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;
        });

        return stats;
    };

    const getTopCategories = () => {
        const courseStats = calculateCourseStats();
        const categories = Object.entries(courseStats.byCategory)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5);

        return categories;
    };

    const exportAnalytics = () => {
        if (!analyticsData) return;

        const csvContent = [
            ["Metric", "Value"].join(","),
            ["Total Users", analyticsData.users.length].join(","),
            ["Total Courses", analyticsData.courses.length].join(","),
            ["Total Events", analyticsData.events.length].join(","),
            ["Total Test Results", analyticsData.testResults.length].join(","),
            ["Published Courses", calculateCourseStats().published].join(","),
            ["Upcoming Events", calculateEventStats().upcoming].join(","),
            ["Average Test Score", calculateTestStats().averageScore].join(",")
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `analytics-${timeRange}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleLogout = async () => {
        await signOut(auth);
    };

    const adminTabs = [
        { id: "overview", label: "Overview", icon: BarChart, path: "/admin" },
        { id: "users", label: "User Management", icon: Users, path: "/admin/users" },
        { id: "courses", label: "Course Management", icon: BookOpen, path: "/admin/courses" },
        { id: "modules", label: "Reading Management", icon: Book, path: "/admin/textmodule" },
        { id: "projects", label: "Project Management", icon: Zap, path: "/admin/projects" },
        { id: "tests", label: "Test Management", icon: Award, path: "/admin/tests" },
        { id: "events", label: "Event Management", icon: Calendar, path: "/admin/events" },
        { id: "announcements", label: "Announcements", icon: Bell, path: "/admin/announcements" },
        { id: "jobs", label: "Job Roles", icon: Briefcase, path: "/admin/jobs" },
        { id: "contact-requests", label: "Contact Requests", icon: Mail, path: "/admin/contact-requests" },
        { id: "profile", label: "Admin Profile", icon: UserCheck, path: "/admin/profile" },
        { id: "companies", label: "Companies", icon: Home, path: "/admin/companies" },
        { id: "gallery", label: "Gallery", icon: Image, path: "/admin/gallery" },
    ];

    // Update active tab based on current route
    useEffect(() => {
        const currentPath = location.pathname;
        const currentTab = adminTabs.find(tab => tab.path === currentPath);
        if (currentTab) {
            setActiveTab(currentTab.id);
        } else {
            setActiveTab("overview");
        }
    }, [location.pathname]);

    if (!isAuthenticated) {
        return <Navigate to="/login" />;
    }

    if (!isAdmin) {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger" role="alert">
                    <h4 className="alert-heading">Access Denied</h4>
                    <p>You don't have permission to access the admin dashboard.</p>
                    <hr />
                    <p className="mb-0">
                        <Link to="/dashboard" className="btn btn-primary">Go to Dashboard</Link>
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.adminContainer}>
            <div className={styles.adminHeader}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.headerTitle}>
                        <Shield size={24} className="me-2" />
                        Admin Dashboard
                    </h2>
                    <p className={styles.headerSubtitle}>
                        Welcome, {userDetails?.name || 'Admin'}
                    </p>
                </div>
                <div className={styles.headerRight}>
                    <div className={styles.notificationIcon}>
                        <Bell size={20} />
                        <span className={styles.notificationBadge}>3</span>
                    </div>
                    <button className={styles.logoutBtn} onClick={handleLogout}>
                        <LogOut size={16} className="me-2" />
                        Logout
                    </button>
                </div>
            </div>

            <div className={styles.adminContent}>
                <div className={styles.sidebar}>
                    <nav className={styles.navMenu}>
                        {adminTabs.map((tab) => (
                            <Link
                                key={tab.id}
                                to={tab.path}
                                className={`${styles.navItem} ${activeTab === tab.id ? styles.active : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <tab.icon size={18} className="me-2" />
                                {tab.label}
                            </Link>
                        ))}
                    </nav>
                </div>

                <div className={styles.mainContent}>
                    {loading ? (
                        <div className={styles.loadingContainer}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2">Loading dashboard data...</p>
                        </div>
                    ) : (
                        <div className={styles.dashboardContent}>
                            {/* Overview Tab Content */}
                            {activeTab === "overview" && (
                                <div>
                                    {/* Analytics Header with Controls */}
                                    {analyticsData && (
                                        <div style={{ marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '1px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <h4 style={{ margin: 0, color: '#2c3e50', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <BarChart size={24} />
                                                    Analytics & Overview
                                                </h4>
                                                <p style={{ margin: '0.5rem 0 0 0', color: '#6c757d', fontSize: '0.95rem' }}>
                                                    Comprehensive insights and metrics
                                                </p>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                <select
                                                    value={timeRange}
                                                    onChange={(e) => setTimeRange(e.target.value)}
                                                    style={{ padding: '0.5rem 1rem', border: '1px solid #dee2e6', borderRadius: '6px', background: 'white', fontSize: '0.9rem', cursor: 'pointer' }}
                                                >
                                                    <option value="7d">Last 7 days</option>
                                                    <option value="30d">Last 30 days</option>
                                                    <option value="90d">Last 90 days</option>
                                                    <option value="1y">Last year</option>
                                                </select>
                                                <button
                                                    onClick={() => { fetchDashboardData(); }}
                                                    title="Refresh Data"
                                                    style={{ width: '40px', height: '40px', border: '1px solid #dee2e6', background: 'white', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6c757d' }}
                                                >
                                                    <RefreshCw size={16} />
                                                </button>
                                                <button
                                                    onClick={exportAnalytics}
                                                    style={{ background: '#28a745', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                                >
                                                    <Download size={16} />
                                                    Export
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div className={styles.statsGrid}>
                                        {/* Users Section */}
                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon}>
                                                <Users className="text-primary" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.totalUsers}</h3>
                                                <p>Total Users</p>
                                                <small className="text-success">
                                                    <TrendingUp size={12} className="me-1" />
                                                    +{stats.newUsersToday} today
                                                </small>
                                            </div>
                                        </div>

                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon}>
                                                <UserCheck className="text-success" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.activeUsers}</h3>
                                                <p>Active Users</p>
                                                <small className="text-muted">
                                                    <Clock size={12} className="me-1" />
                                                    Last 7 days
                                                </small>
                                            </div>
                                        </div>

                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon}>
                                                <Shield className="text-danger" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.adminCount}</h3>
                                                <p>Admins</p>
                                                <small className="text-muted">
                                                    {stats.studentCount} Students, {stats.professionalCount} Professionals
                                                </small>
                                            </div>
                                        </div>

                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon} style={{ background: 'rgba(220, 53, 69, 0.1)' }}>
                                                <Users className="text-danger" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.deletedUsers}</h3>
                                                <p>Deleted Accounts</p>
                                                <small className="text-muted">
                                                    {stats.deactivatedUsers} Deactivated
                                                </small>
                                            </div>
                                        </div>

                                        {/* Courses Section */}
                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon}>
                                                <BookOpen className="text-info" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.totalCourses}</h3>
                                                <p>Total Courses</p>
                                                <small className="text-success">
                                                    {stats.publishedCourses} Published
                                                </small>
                                            </div>
                                        </div>

                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon}>
                                                <Award className="text-warning" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.totalEnrollments}</h3>
                                                <p>Total Enrollments</p>
                                                <small className="text-muted">
                                                    {stats.draftCourses} Drafts
                                                </small>
                                            </div>
                                        </div>

                                        {/* Events Section */}
                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon}>
                                                <Calendar className="text-warning" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.totalEvents}</h3>
                                                <p>Total Events</p>
                                                <small className="text-success">
                                                    {stats.upcomingEvents} Upcoming
                                                </small>
                                            </div>
                                        </div>

                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon} style={{ background: 'rgba(108, 117, 125, 0.1)' }}>
                                                <Calendar className="text-muted" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.pastEvents}</h3>
                                                <p>Past Events</p>
                                                <small className="text-muted">
                                                    Completed
                                                </small>
                                            </div>
                                        </div>

                                        {/* Tests & Modules Section */}
                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon} style={{ background: 'rgba(255, 193, 7, 0.1)' }}>
                                                <Activity className="text-warning" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.totalTests}</h3>
                                                <p>Total Tests</p>
                                                <small className="text-muted">
                                                    Assessment Quizzes
                                                </small>
                                            </div>
                                        </div>

                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon} style={{ background: 'rgba(13, 110, 253, 0.1)' }}>
                                                <Book className="text-primary" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.totalModules}</h3>
                                                <p>Text Modules</p>
                                                <small className="text-muted">
                                                    Reading Materials
                                                </small>
                                            </div>
                                        </div>

                                        {/* Projects Section */}
                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon} style={{ background: 'rgba(102, 126, 234, 0.1)' }}>
                                                <Zap className="text-primary" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.totalProjects}</h3>
                                                <p>Total Projects</p>
                                                <small className="text-muted">
                                                    {stats.userProjects} User, {stats.adminProjects} Admin
                                                </small>
                                            </div>
                                        </div>

                                        {/* Announcements Section */}
                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon} style={{ background: 'rgba(220, 53, 69, 0.1)' }}>
                                                <Bell className="text-danger" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.totalAnnouncements}</h3>
                                                <p>Announcements</p>
                                                <small className="text-muted">
                                                    Platform Updates
                                                </small>
                                            </div>
                                        </div>

                                        {/* Jobs & Contacts Section */}
                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon} style={{ background: 'rgba(25, 135, 84, 0.1)' }}>
                                                <Briefcase className="text-success" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.totalJobs}</h3>
                                                <p>Job Roles</p>
                                                <small className="text-muted">
                                                    Career Opportunities
                                                </small>
                                            </div>
                                        </div>

                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon} style={{ background: 'rgba(13, 202, 240, 0.1)' }}>
                                                <Mail className="text-info" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.totalContacts}</h3>
                                                <p>Contact Requests</p>
                                                <small className="text-warning">
                                                    {stats.pendingContacts} Pending
                                                </small>
                                            </div>
                                        </div>

                                        {/* Companies & Gallery Section */}
                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon} style={{ background: 'rgba(108, 117, 125, 0.1)' }}>
                                                <Home className="text-dark" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.totalCompanies}</h3>
                                                <p>Companies</p>
                                                <small className="text-muted">
                                                    Partner Organizations
                                                </small>
                                            </div>
                                        </div>

                                        <div className={styles.statCard}>
                                            <div className={styles.statIcon} style={{ background: 'rgba(214, 51, 132, 0.1)' }}>
                                                <Image className="text-danger" />
                                            </div>
                                            <div className={styles.statContent}>
                                                <h3>{stats.totalGallery}</h3>
                                                <p>Gallery Items</p>
                                                <small className="text-muted">
                                                    Media Assets
                                                </small>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Analytics Charts */}
                                    {analyticsData && (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                                            {/* User Growth Chart */}
                                            <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid #e9ecef' }}>
                                                <div style={{ marginBottom: '1rem' }}>
                                                    <h5 style={{ margin: '0 0 0.25rem 0', color: '#2c3e50', fontWeight: 600 }}>User Growth</h5>
                                                    <small style={{ color: '#6c757d', fontSize: '0.85rem' }}>New registrations over time</small>
                                                </div>
                                                <div style={{ height: '300px', position: 'relative' }}>
                                                    <Line
                                                        data={{
                                                            labels: calculateUserGrowth().map(d => d.date),
                                                            datasets: [{
                                                                label: "New Users",
                                                                data: calculateUserGrowth().map(d => d.count),
                                                                borderColor: "rgb(75, 192, 192)",
                                                                backgroundColor: "rgba(75, 192, 192, 0.2)",
                                                                tension: 0.1
                                                            }]
                                                        }}
                                                        options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } } }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Course Categories Chart */}
                                            <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid #e9ecef' }}>
                                                <div style={{ marginBottom: '1rem' }}>
                                                    <h5 style={{ margin: '0 0 0.25rem 0', color: '#2c3e50', fontWeight: 600 }}>Course Categories</h5>
                                                    <small style={{ color: '#6c757d', fontSize: '0.85rem' }}>Distribution by category</small>
                                                </div>
                                                <div style={{ height: '300px', position: 'relative' }}>
                                                    <Doughnut
                                                        data={{
                                                            labels: getTopCategories().map(([category]) => category),
                                                            datasets: [{
                                                                data: getTopCategories().map(([, count]) => count),
                                                                backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF"]
                                                            }]
                                                        }}
                                                        options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Event Types Chart */}
                                            <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid #e9ecef' }}>
                                                <div style={{ marginBottom: '1rem' }}>
                                                    <h5 style={{ margin: '0 0 0.25rem 0', color: '#2c3e50', fontWeight: 600 }}>Event Types</h5>
                                                    <small style={{ color: '#6c757d', fontSize: '0.85rem' }}>Distribution by type</small>
                                                </div>
                                                <div style={{ height: '300px', position: 'relative' }}>
                                                    <Pie
                                                        data={{
                                                            labels: Object.keys(calculateEventStats().byType),
                                                            datasets: [{
                                                                data: Object.values(calculateEventStats().byType),
                                                                backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40"]
                                                            }]
                                                        }}
                                                        options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Project Categories Chart */}
                                            <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid #e9ecef' }}>
                                                <div style={{ marginBottom: '1rem' }}>
                                                    <h5 style={{ margin: '0 0 0.25rem 0', color: '#2c3e50', fontWeight: 600 }}>Project Categories</h5>
                                                    <small style={{ color: '#6c757d', fontSize: '0.85rem' }}>Projects distribution by category</small>
                                                </div>
                                                <div style={{ height: '300px', position: 'relative' }}>
                                                    <Bar
                                                        data={{
                                                            labels: Object.keys(calculateProjectStats().byCategory),
                                                            datasets: [{
                                                                label: 'Number of Projects',
                                                                data: Object.values(calculateProjectStats().byCategory),
                                                                backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40", "#4ECDC4", "#95E1D3"],
                                                                borderColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40", "#4ECDC4", "#95E1D3"],
                                                                borderWidth: 1
                                                            }]
                                                        }}
                                                        options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } } }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Insights Section */}
                                    {analyticsData && (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                                            {/* Top Categories */}
                                            <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid #e9ecef' }}>
                                                <h6 style={{ margin: '0 0 1rem 0', color: '#2c3e50', fontWeight: 600, fontSize: '1rem' }}>Top Performing Categories</h6>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                    {getTopCategories().map(([category, count], index) => (
                                                        <div key={category} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                                                            <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#667eea', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 600 }}>
                                                                #{index + 1}
                                                            </span>
                                                            <span style={{ flex: 1, fontWeight: 500, color: '#495057' }}>{category}</span>
                                                            <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>{count} courses</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Activity Summary */}
                                            <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid #e9ecef' }}>
                                                <h6 style={{ margin: '0 0 1rem 0', color: '#2c3e50', fontWeight: 600, fontSize: '1rem' }}>Recent Activity Summary</h6>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                                                        <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>New Users Today:</span>
                                                        <span style={{ fontWeight: 600, color: '#2c3e50', fontSize: '1.1rem' }}>
                                                            {calculateUserGrowth().slice(-1)[0]?.count || 0}
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                                                        <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>Courses Published:</span>
                                                        <span style={{ fontWeight: 600, color: '#2c3e50', fontSize: '1.1rem' }}>{calculateCourseStats().published}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                                                        <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>Upcoming Events:</span>
                                                        <span style={{ fontWeight: 600, color: '#2c3e50', fontSize: '1.1rem' }}>{calculateEventStats().upcoming}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                                                        <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>Avg Test Score:</span>
                                                        <span style={{ fontWeight: 600, color: '#2c3e50', fontSize: '1.1rem' }}>{calculateTestStats().averageScore}%</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className={styles.dashboardGrid}>
                                        <div className={styles.recentUsers}>
                                            <h5 className={styles.sectionTitle}>Recent Users</h5>
                                            <div className={styles.userList}>
                                                {recentUsers.map((user) => (
                                                    <div key={user.id} className={styles.userItem}>
                                                        <div className={styles.userAvatar}>
                                                            <img
                                                                src={user.profileImage || "/static_img/DP.jpg"}
                                                                alt={user.name || "User"}
                                                                onError={(e) => {
                                                                    e.target.src = "/static_img/DP.jpg";
                                                                }}
                                                            />
                                                        </div>
                                                        <div className={styles.userInfo}>
                                                            <h6>{user.name || user.email}</h6>
                                                            <p>{user.email}</p>
                                                            <small className="text-muted">
                                                                Joined: {user.createdAt ? user.createdAt.toDate().toLocaleDateString() : 'N/A'}
                                                            </small>
                                                        </div>
                                                        <div className={styles.userStatus}>
                                                            <span className={`badge ${user.role === 'admin' ? 'bg-danger' : 'bg-primary'}`}>
                                                                {user.role || 'user'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className={styles.recentActivities}>
                                            <h5 className={styles.sectionTitle}>Recent Activities</h5>
                                            <div className={styles.activityList}>
                                                {recentActivities.map((activity) => (
                                                    <div key={activity.id} className={styles.activityItem}>
                                                        <div className={styles.activityIcon}>
                                                            {activity.type === 'user' && <Users size={16} />}
                                                            {activity.type === 'course' && <BookOpen size={16} />}
                                                            {activity.type === 'event' && <Calendar size={16} />}
                                                            {activity.type === 'test' && <Award size={16} />}
                                                            {activity.type === 'enrollment' && <UserCheck size={16} />}
                                                        </div>
                                                        <div className={styles.activityContent}>
                                                            <p className="mb-1">{activity.action}</p>
                                                            <small className="text-muted">{activity.time}</small>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Render child routes */}
                            <Outlet />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminDashboard;
